import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';

function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPending();
  }, []);

  const fetchPending = async () => {
    try {
      const res = await api.get('/Payment/admin/pending');
      setPayments(res.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (paymentId) => {
    setActionLoading(paymentId);
    try {
      await api.post(`/Payment/admin/approve/${paymentId}`, {
        adminNotes: 'Approved by Admin',
      });
      toast.success('Payment Approved! User activated.');
      fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Approve failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (paymentId) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;

    setActionLoading(paymentId);
    try {
      await api.post(`/Payment/admin/reject/${paymentId}`, {
        reason: reason,
      });
      toast.success('Payment Rejected');
      fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reject failed');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Admin – Pending Payments</h1>
            <p className="text-gray-500 text-sm">Approve or Reject UTR payments</p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm bg-gray-200 px-4 py-2 rounded-lg"
          >
            ← Back
          </button>
        </div>

        {/* Count */}
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
          <p className="text-orange-700 font-semibold">
            Pending Payments: {payments.length}
          </p>
        </div>

        {/* List */}
        {payments.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center shadow">
            <p className="text-gray-400 text-lg">No pending payments</p>
          </div>
        ) : (
          <div className="space-y-4">
            {payments.map((p) => (
              <div key={p.id} className="bg-white rounded-2xl p-5 shadow">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div>
                    <p className="text-xs text-gray-500">User</p>
                    <p className="font-bold">{p.user?.fullName}</p>
                    <p className="text-sm text-gray-500">{p.user?.mobile}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">UTR ID</p>
                    <p className="font-mono font-bold text-blue-600">{p.utrId}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Amount</p>
                    <p className="text-xl font-bold">₹{p.amount}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Gateway</p>
                    <p className="font-semibold">{p.gateway}</p>
                    <p className="text-xs text-gray-400">
                      {new Date(p.createdAt).toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => handleApprove(p.id)}
                    disabled={actionLoading === p.id}
                    className="flex-1 bg-green-600 text-white font-bold py-2 rounded-xl disabled:opacity-50"
                  >
                    {actionLoading === p.id ? '...' : '✅ Approve'}
                  </button>
                  <button
                    onClick={() => handleReject(p.id)}
                    disabled={actionLoading === p.id}
                    className="flex-1 bg-red-500 text-white font-bold py-2 rounded-xl disabled:opacity-50"
                  >
                    {actionLoading === p.id ? '...' : '❌ Reject'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPayments;